import { FunctionComponent, useCallback, useEffect } from 'react';
import PaginatedTable from 'src/shared/Table/PaginatedTable';
import QuestTableHeader from './QuestTableHeader';
import QuestTableRow from './QuestTableRow';
import useAppDispatch from 'src/hooks/useAppDispatch';
import { fetchProjectQuests } from 'src/redux/client/service';
import { ClientSelectors } from 'src/redux/client/selectors';
import {
    setQuestsRowsPerPage,
    setQuestsCurrentPage
} from 'src/redux/client/slice';
import { ClientQuest } from 'src/types/client';

export interface Props {}

const Requests: FunctionComponent<Props> = ({}: Props) => {
    const dispatch = useAppDispatch();

    const {
        quests,
        totalQuests,
        selectedProjectId,
        questsRowsPerPage,
        questsSearchQuery,
        questsStatusFilter,
        questsCurrentPage
    } = ClientSelectors();

    useEffect(() => {
        if (selectedProjectId) {
            dispatch(
                fetchProjectQuests({
                    page: questsCurrentPage || 0,
                    resultsPerPage: questsRowsPerPage,
                    q: questsSearchQuery,
                    statusFilters: questsStatusFilter || []
                })
            );
        }
    }, [
        dispatch,
        selectedProjectId,
        questsRowsPerPage,
        questsSearchQuery,
        questsStatusFilter,
        questsCurrentPage
    ]);

    const handlePageChange = useCallback(
        async (page: number) => {
            await dispatch(
                fetchProjectQuests({
                    page,
                    resultsPerPage: questsRowsPerPage,
                    q: questsSearchQuery,
                    statusFilters: questsStatusFilter || []
                })
            ).unwrap();
            dispatch(setQuestsCurrentPage(page));
        },
        [dispatch, questsRowsPerPage, questsSearchQuery, questsStatusFilter]
    );

    const handleRowsPerPageChange = useCallback(
        (rowsPerPage: number) => {
            dispatch(setQuestsRowsPerPage(rowsPerPage));
        },
        [dispatch]
    );

    const sortComparator = useCallback(
        (
            a: ClientQuest,
            b: ClientQuest,
            sortBy: string,
            direction: 'asc' | 'desc'
        ) => {
            if (sortBy === 'title') {
                return direction === 'asc'
                    ? a.quest.title.localeCompare(b.quest.title)
                    : b.quest.title.localeCompare(a.quest.title);
            }
            if (sortBy === 'dateCreated') {
                return direction === 'asc'
                    ? a.quest.created.localeCompare(b.quest.created)
                    : b.quest.created.localeCompare(a.quest.created);
            }
            if (sortBy === 'netTime') {
                return direction === 'asc'
                    ? a.netTime - b.netTime
                    : b.netTime - a.netTime;
            }
            if (sortBy === 'status') {
                return direction === 'asc'
                    ? a.quest.status - b.quest.status
                    : b.quest.status - a.quest.status;
            }
            if (sortBy === 'priority') {
                return direction === 'asc'
                    ? a.quest.priority - b.quest.priority
                    : b.quest.priority - a.quest.priority;
            }
            return 0;
        },
        []
    );

    return (
        <PaginatedTable<ClientQuest>
            data={quests || []}
            totalCount={totalQuests || 0}
            currentPage={questsCurrentPage}
            rowsPerPage={questsRowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            sortComparator={sortComparator}
            renderHeader={(props) => <QuestTableHeader {...props} />}
            renderRow={(q, i) => <QuestTableRow clientQuest={q} key={i} />}
            ariaLabel="requests table"
        />
    );
};

export default Requests;
